import React, { useEffect, useState } from 'react';
import axios from 'axios';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import SidePanel from '../../components/SidePanel/SidePanel';
import DataTable from '../../components/DataTable/DataTable';
import { apiList, encrypt, verifyLogin } from '../../helpers';

// Side panel with the rows behind a Home card: pending activities or
// properties without a location. Server-paged; a row opens its loan.

const PAGE_SIZES = [10, 25, 50, 100];

const PENDING_COLUMNS = [
  { id: 'loan', label: 'Loan Number' },
  { id: 'customer', label: 'Customer' },
  { id: 'branch', label: 'Branch' },
  { id: 'activity', label: 'Activity' },
  { id: 'where', label: 'Floor / Component' },
  { id: 'status', label: 'Status' },
  { id: 'upload', label: 'Last upload' },
];

const MISSING_COLUMNS = [
  { id: 'loan', label: 'Loan Number' },
  { id: 'customer', label: 'Customer' },
  { id: 'branch', label: 'Branch' },
  { id: 'city', label: 'City' },
  { id: 'district', label: 'District' },
  { id: 'state', label: 'State' },
];

const STAGES = [
  { value: 'all', label: 'All' },
  { value: 'Submitted', label: 'Submitted' },
  { value: 'Uploading', label: 'Uploading' },
];

export default function DashboardListPanel({
  kind, open, onClose, params, initialStatus = 'all', history,
}) {
  const [status, setStatus] = useState(initialStatus);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState({ rows: [], totalCount: 0 });
  const [loading, setLoading] = useState(false);
  const pending = kind === 'pending';

  // Reset when the panel is opened (possibly on a different stage).
  useEffect(() => {
    if (open) {
      setStatus(initialStatus);
      setPage(1);
    }
  }, [open, initialStatus]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoading(true);
    const { session } = verifyLogin();
    axios.get(pending ? apiList.DASHBOARD_PENDING : apiList.DASHBOARD_MISSING_LOCATION, {
      headers: { session },
      params: {
        ...params, page, limit: pageSize, ...(pending ? { status } : {}),
      },
    })
      .then((response) => { if (active) setData(response.data.data); })
      .catch(() => { if (active) setData({ rows: [], totalCount: 0 }); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, pending, status, page, pageSize, params]);

  const openLoan = (loanId) => history.push(`/view-loan-details/${encrypt(loanId)}`);

  const rows = data.rows.map((row) => (
    <TableRow
      hover
      key={pending ? row.constructionPlanId : row.propertyId}
      className='home-link-row'
      onClick={() => openLoan(row.loanId)}
    >
      <TableCell><span className='home-loan-link'>{row.loanNumber || 'N/A'}</span></TableCell>
      <TableCell>{row.custName || 'N/A'}</TableCell>
      <TableCell>{row.branchName || 'N/A'}</TableCell>
      {pending ? (
        <>
          <TableCell>{[row.activityTypeConfigName, row.activityConfigName].filter(Boolean).join(' · ')}</TableCell>
          <TableCell>
            {[row.propertyComponentFloorName, row.propertyComponentName].filter(Boolean).join(' · ') || '—'}
          </TableCell>
          <TableCell><span className={`home-status ${row.status}`}>{row.status}</span></TableCell>
          <TableCell>{row.lastUpload || '—'}</TableCell>
        </>
      ) : (
        <>
          <TableCell>{row.cityTown || '—'}</TableCell>
          <TableCell>{row.district || '—'}</TableCell>
          <TableCell>{row.state || '—'}</TableCell>
        </>
      )}
    </TableRow>
  ));

  return (
    <SidePanel
      open={open}
      setOpen={(value) => { if (!value) onClose(); }}
      title={pending ? 'Pending activities' : 'Properties without location'}
      subtitle={pending
        ? 'Activities waiting for review (Submitted) or still uploading from the app. Select a row to open the loan.'
        : 'Loan properties with no latitude / longitude. Select a row to open the loan.'}
      width={980}
      showAction={false}
      body={(
        <>
          {pending ? (
            <div className='home-list-tools'>
              <ToggleButtonGroup
                className='home-range'
                size='small'
                exclusive
                value={status}
                onChange={(e, value) => { if (value) { setStatus(value); setPage(1); } }}
              >
                {STAGES.map((s) => (
                  <ToggleButton key={s.value} value={s.value}>{s.label}</ToggleButton>
                ))}
              </ToggleButtonGroup>
              <span>Most recent upload first</span>
            </div>
          ) : null}
          <DataTable
            bare
            serverSide
            alwaysShowPager
            loading={loading}
            tableHead={pending ? PENDING_COLUMNS : MISSING_COLUMNS}
            itemComponent={rows}
            noDataText='Nothing here.'
            page={page}
            count={Math.ceil((data.totalCount || 0) / pageSize)}
            totalCount={data.totalCount}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZES}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            handleChangePage={(e, newPage) => setPage(newPage)}
          />
        </>
      )}
    />
  );
}
